import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import { usePortfolioData } from '../../../components/data/PortfolioData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapLocationDot, faHeartPulse, faTableColumns, faTable, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import TransformerTabs from '../../../components/layout/TransformerTabs';
import SectorList from '../../../components/layout/SectorList';
import SectorTable from '../../../components/layout/SectorTable';
import SectorPowerBlocks from '../../../components/layout/SectorPowerBlocks';
import { apiFetch } from '../../../api/apiClient';
import { toSlug } from '../../../util/strings';
import SiteOutputMonitor from '../../../components/graphs/SiteOutputMonitor';
import ChartTooltip from '../../../components/layout/ChartTooltip';
import Tooltip from '../../../components/layout/Tooltip';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import { usePortfolioDateFormatter } from '../../../utils/usePortfolioDateFormatter';

function normalizePortfolioKey(value = '') {
  return String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
}

function Plant({ plantName }) {
  const { plant: plantData } = usePlantData();
  const { data: portfolios } = usePortfolioData();
  const [transformerView, setTransformerView] = useState(true);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const [activeXFMR, setActiveXFMR] = useState(0);
  const [sectors, setSectors] = useState([]);
  const [siteOutputPeriod, setSiteOutputPeriod] = useState('today');
  const navigate = useNavigate();
  const sectorId = activeXFMR + 1;
  const portfolioName = toSlug(plantName) ?? "";
  const dateFormatter = usePortfolioDateFormatter({ portfolioName });
  const lastUpdated = useMemo(() => {
    const normalized = normalizePortfolioKey(portfolioName);
    const match = (portfolios ?? []).find((node) => (
      normalizePortfolioKey(node.portfolio_name) === normalized
      || normalizePortfolioKey(node.plant_name) === normalized
    ));
    return match?.last_updated ?? null;
  }, [portfolios, portfolioName]);

  const toggleTansformerView = () => {
    setTransformerView((prev) => !prev);
  };

  useEffect(() => {
    const titlePlantName = (plantData?.plant_name ?? portfolioName ?? "Plant");
    document.title = `Vivosol Plant Performance Insights Hub | ${titlePlantName} / Monitoring / Plant`;
  }, [plantData?.plant_name, portfolioName]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const raw = await apiFetch(`/api/metrics/portfolio/${portfolioName}/sectors`);
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.sectors) ? raw.sectors : [];
        if (!cancelled) {
          setSectors(list);
        }
      } catch (apiError) {
        console.warn(`API fetch failed for /api/metrics/portfolio/${portfolioName}/sectors:`, apiError);
        if (!cancelled) {
          setSectors([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [portfolioName]);

  useEffect(() => {
    if (!sectors.length) {
      return;
    }
    setActiveXFMR((prev) => (prev < sectors.length ? prev : 0));
  }, [sectors]);

  return (
    <SidebarPageLayout id="monitoringPant">
      <div className="content p-5">
        <ChartTooltip tooltip={tooltip} transition={false} />
        
        <Marquee />
        
        <div className="d-flex justify-content-between align-items-start gap-3 mt-3">
          <h1 className="mb-0"><Link to={`${plantData.dashboard}`}><strong>{plantData.plant_name}</strong></Link> : {plantData.config} ({plantData.location})</h1>
          {lastUpdated ? (
            <span className="text-nowrap small pt-1" title="KPI last updated">
              <strong>Last Updated:</strong> {dateFormatter.formatUtc(lastUpdated)}
            </span>
          ) : null}
        </div>
        <Breadcrumbs labels={{ portfolioName: plantData.plant_name }} />
        
        <div id="plantwhole" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3">
            <span><FontAwesomeIcon icon={faHeartPulse} /> PLANT</span>
            <div className="right d-flex align-items-stretch gap-2">
              <button className="d-none"><FontAwesomeIcon icon={faMapLocationDot} /></button> <button onClick={() => navigate(plantData.dashboard)}><FontAwesomeIcon icon={faTableColumns} /></button>
            </div>
          </div>
          <div className="body pt-3">
            <div className="d-flex gap-5 justify-content-between align-items-center">
            <div className="col-8 text-start">
              <h2>
                Site Output Monitoring ({siteOutputPeriod === 'today' ? 'Today' : 'Yesterday'}) -{' '}
                {siteOutputPeriod === 'today' ? (
                  <button type="button" className="" onClick={() => setSiteOutputPeriod('yesterday')}>
                    View Yesterday
                  </button>
                ) : (
                  <button type="button" className="" onClick={() => setSiteOutputPeriod('today')}>
                    View Today
                  </button>
                )}
              </h2>
              <SiteOutputMonitor
                portfolioName={portfolioName}
                onTooltip={setTooltip}
                day={siteOutputPeriod}
              />
            </div>
            <div className="plantMetric col text-start">
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>INVERTERS</strong>{plantData.inverters_online} / {plantData.inverters_offline}</div>
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>DCDC CONVERTERS</strong>{plantData.dcdc_converters_online} / {plantData.dcdc_converters_offline}</div>
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>BATTERY RACKS</strong>{plantData.battery_racks_online} / {plantData.battery_racks_offline} </div>
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>PV SETPOINT</strong>{plantData.pv_setpoint} MW</div>
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>BESS SETPOINT</strong>{plantData.bess_setpoint} MW</div>
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>BESS CHARGE</strong>{plantData.bess_charge} MW</div>
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>BESS DISCHARGE</strong>{plantData.bess_discharge} MW</div>
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                <strong>CURTAILMENT TODAY</strong>
                <span className="d-flex align-items-center gap-1">
                  {plantData.curtailment_today} MW
                  <Tooltip position="left" content="900 MW AC nameplate minus PV setpoint. Do not use 858 (DCDC count) or 968.1 (DC rating).">
                    <button type="button"><FontAwesomeIcon icon={faInfoCircle} /></button>
                  </Tooltip>
                </span>
              </div>
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between"><strong>GENERATION</strong>{plantData.plant_generation_peak} MW / {plantData.plant_generation_total} MWh</div>
            </div>
            </div>
          </div>
        </div>

        <div id="plantsectors" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faHeartPulse} /> SECTOR</span>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <button className="d-none"><FontAwesomeIcon icon={faMapLocationDot} /></button> <button onClick={toggleTansformerView} ><FontAwesomeIcon icon={transformerView ? faTableColumns : faTable} /></button>
            </div>
          </div>
          <div className={`body gap-5 justify-content-between pt-3 ${ transformerView ? 'd-flex' : 'd-none' }`}>
            <TransformerTabs
              activeXFMR={activeXFMR}
              setActiveXFMR={setActiveXFMR}
              sectors={sectors}
              portfolioName={portfolioName}
              onTooltip={setTooltip}
            />
            <div className="col text-start">
              {sectors[activeXFMR] && <SectorList sectorData={sectors[activeXFMR]} />}
            </div>
          </div>
          <div className={`body pt-3 ${ transformerView ? 'd-none' : 'd-block' }`}>
            <SectorTable activeXFMR={activeXFMR} setActiveXFMR={setActiveXFMR} sectors={sectors} />
          </div>
        </div>
        <SectorPowerBlocks sectorId={sectorId} portfolioName={portfolioName} onTooltip={setTooltip} />
      </div>
    </SidebarPageLayout>
  );
}

export default Plant;
